(function initDabovDeliverySchedule(global) {
  const DEFAULT_TIME_ZONE = 'Europe/Istanbul';
  const DAY_INDEX = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

  function zonedParts(date, requestedTimeZone = DEFAULT_TIME_ZONE) {
    let formatter;
    let timeZone = requestedTimeZone || DEFAULT_TIME_ZONE;
    try {
      formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        hourCycle: 'h23',
        weekday: 'short',
      });
    } catch (_error) {
      timeZone = DEFAULT_TIME_ZONE;
      formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        hourCycle: 'h23',
        weekday: 'short',
      });
    }

    const values = Object.fromEntries(
      formatter
        .formatToParts(date)
        .filter((part) => part.type !== 'literal')
        .map((part) => [part.type, part.value]),
    );

    return {
      year: Number(values.year),
      month: Number(values.month),
      day: Number(values.day),
      hour: Number(values.hour),
      weekday: DAY_INDEX[values.weekday],
      timeZone,
      ymd: `${values.year}-${values.month}-${values.day}`,
    };
  }

  function parseHolidays(value = '') {
    return new Set(
      String(value)
        .split(/[\s,;]+/)
        .map((entry) => entry.trim())
        .filter((entry) => /^\d{4}-\d{2}-\d{2}$/.test(entry)),
    );
  }

  function addDays(parts, amount) {
    const date = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + amount, 12));
    const weekday = date.getUTCDay();
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth() + 1;
    const day = date.getUTCDate();
    return {
      year,
      month,
      day,
      weekday,
      ymd: `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
    };
  }

  function isWorkingDay(parts, options) {
    if (options.holidays.has(parts.ymd)) return false;
    if (parts.weekday === 0) return options.sundayWorking;
    if (parts.weekday === 6) return options.saturdayWorking;
    return true;
  }

  function nextWorkingDay(parts, options) {
    for (let offset = 1; offset <= 370; offset += 1) {
      const candidate = addDays(parts, offset);
      if (isWorkingDay(candidate, options)) return candidate;
    }
    return null;
  }

  function formatWorkingDate(parts, locale = 'tr-TR') {
    if (!parts) return '';
    const date = new Date(Date.UTC(parts.year, parts.month - 1, parts.day, 12));
    return new Intl.DateTimeFormat(locale, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    }).format(date);
  }

  function evaluate(date, options) {
    const now = zonedParts(date, options.timeZone);
    const workingToday = isWorkingDay(now, options);
    if (workingToday && now.hour < options.cutoffHour) {
      return { state: 'same-day', now, next: null };
    }
    return {
      state: workingToday ? 'after-cutoff' : 'non-working',
      now,
      next: nextWorkingDay(now, options),
    };
  }

  function templateMessage(template, next, locale, cutoffHour = 18) {
    const cutoff = `${String(cutoffHour).padStart(2, '0')}.00`;
    return String(template || '')
      .replaceAll('[next_workday]', formatWorkingDate(next, locale))
      .replaceAll('[cutoff]', cutoff);
  }

  const api = { zonedParts, parseHolidays, addDays, isWorkingDay, nextWorkingDay, formatWorkingDate, evaluate, templateMessage };
  global.DabovDeliverySchedule = api;

  if (typeof global.HTMLElement === 'undefined' || !global.customElements) return;

  class DabovDeliveryMessage extends global.HTMLElement {
    connectedCallback() {
      this.output = this.querySelector('[data-dispatch-message]');
      if (!this.output) return;
      this.update();
      this.timer = global.setInterval(() => this.update(), 60_000);
      this.onVisibilityChange = () => {
        if (!global.document.hidden) this.update();
      };
      global.document.addEventListener('visibilitychange', this.onVisibilityChange);
    }

    disconnectedCallback() {
      if (this.timer) global.clearInterval(this.timer);
      if (this.onVisibilityChange) global.document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }

    options() {
      return {
        timeZone: this.dataset.timeZone || DEFAULT_TIME_ZONE,
        cutoffHour: Number(this.dataset.cutoffHour || 18),
        saturdayWorking: this.dataset.saturdayWorking === 'true',
        sundayWorking: this.dataset.sundayWorking === 'true',
        holidays: parseHolidays(this.dataset.holidays),
      };
    }

    update(now = new Date()) {
      const result = evaluate(now, this.options());
      const locale = this.dataset.dateLocale || 'tr-TR';
      let template = this.dataset.beforeCutoffTemplate;
      if (result.state === 'after-cutoff') template = this.dataset.afterCutoffTemplate;
      if (result.state === 'non-working') template = this.dataset.nonWorkingTemplate;
      this.output.textContent = templateMessage(template, result.next, locale, this.options().cutoffHour);
      this.dataset.dispatchState = result.state;
    }
  }

  if (!global.customElements.get('dabov-delivery-message')) {
    global.customElements.define('dabov-delivery-message', DabovDeliveryMessage);
  }
})(typeof window !== 'undefined' ? window : globalThis);
